import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Sparkles, Mail, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = ["Trading", "Manufacturing", "Services", "SaaS / Tech", "Consulting", "Retail", "E-commerce", "Construction", "Logistics", "Healthcare", "Hospitality", "Other"];

// Official GSTIN format: 2-digit state code + 10-char PAN + entity-number + Z + checksum.
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const isValidGstin = (v) => GSTIN_RE.test((v || "").trim());
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Password policy — kept in lock-step with backend server.py signup rules.
// Special-char class matches ASCII punctuation ranges: !-/  :-@  [-`  {-~
const PASSWORD_SPECIAL_RE = /[!-/:-@[-`{-~]/;
function validatePassword(pw) {
    const missing = [];
    if (!pw || pw.length < 8) missing.push("at least 8 characters");
    if (!/[A-Z]/.test(pw)) missing.push("one uppercase letter");
    if (!/[a-z]/.test(pw)) missing.push("one lowercase letter");
    if (!/\d/.test(pw)) missing.push("one number");
    if (!PASSWORD_SPECIAL_RE.test(pw)) missing.push("one special character (e.g. !@#$%^&*)");
    return { ok: missing.length === 0, missing };
}

export default function Signup() {
    const [step, setStep] = useState(1);                 // 1 = About you, 2 = Verify email, 3 = About your business
    const [busy, setBusy] = useState(false);
    const [gstinError, setGstinError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [otp, setOtp] = useState("");
    const [otpError, setOtpError] = useState("");
    const [submitError, setSubmitError] = useState("");
    const [cooldown, setCooldown] = useState(0);         // seconds
    const cooldownRef = useRef(null);
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        password: "",
        org_name: "",
        legal_name: "",
        base_currency: "INR",
        industry: "Trading",
        gstin: "",
    });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Cooldown ticker for "Resend code"
    useEffect(() => {
        if (cooldown <= 0) return;
        cooldownRef.current = setTimeout(() => setCooldown((s) => s - 1), 1000);
        return () => clearTimeout(cooldownRef.current);
    }, [cooldown]);

    // Step 1 → Step 2: verify email deliverability + send OTP
    const goToVerify = async () => {
        setEmailError("");
        setPasswordError("");
        if (!form.full_name.trim()) { toast.error("Enter your full name."); return; }
        if (!EMAIL_RE.test(form.email)) { setEmailError("Enter a valid email address."); return; }
        const pw = validatePassword(form.password);
        if (!pw.ok) {
            const msg = "Password must include " + pw.missing.join(", ") + ".";
            setPasswordError(msg);
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${BACKEND_URL}/api/auth/verify-email-start`, { email: form.email.trim().toLowerCase() });
            setStep(2);
            setOtp("");
            setOtpError("");
            setCooldown(45);
            toast.success("We sent a 6-digit code to your inbox.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not send verification code. Try again.";
            setEmailError(msg);
            toast.error(msg);
        } finally { setBusy(false); }
    };

    // Resend OTP (Step 2)
    const resendOtp = async () => {
        if (cooldown > 0) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${BACKEND_URL}/api/auth/verify-email-start`, { email: form.email.trim().toLowerCase() });
            setCooldown(data?.resend_cooldown_seconds || 45);
            setOtpError("");
            toast.success("New code sent.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not resend. Try again in a moment.";
            toast.error(msg);
            if (err?.response?.status === 429) {
                // extract the seconds hint if present
                const m = /(\d+)\s*s/.exec(msg);
                if (m) setCooldown(parseInt(m[1], 10));
            }
        } finally { setBusy(false); }
    };

    // Step 2 → Step 3: check OTP
    const verifyOtp = async (e) => {
        e?.preventDefault?.();
        if (!/^\d{6}$/.test(otp)) { setOtpError("Enter the 6-digit code."); return; }
        setBusy(true);
        try {
            await axios.post(`${BACKEND_URL}/api/auth/verify-email-check`, {
                email: form.email.trim().toLowerCase(),
                code: otp,
            });
            setStep(3);
            toast.success("Email verified.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not verify the code.";
            setOtpError(msg);
        } finally { setBusy(false); }
    };

    // Step 3 → create workspace
    const submit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        if (!form.org_name.trim()) {
            const msg = "Organization name is required.";
            setSubmitError(msg); toast.error(msg); return;
        }
        if (!form.gstin.trim()) {
            const msg = "GSTIN is required to set up your workspace.";
            setGstinError(msg); setSubmitError(msg); toast.error(msg); return;
        }
        if (!isValidGstin(form.gstin)) {
            const msg = "Enter a valid 15-character GSTIN (e.g. 27AAAAA0000A1Z5).";
            setGstinError(msg); setSubmitError(msg); toast.error(msg); return;
        }
        setGstinError("");
        setBusy(true);
        try {
            const { data } = await axios.post(`${BACKEND_URL}/api/auth/signup`, {
                ...form,
                username: form.email.split("@")[0],
            });
            if (!data?.access_token) {
                throw new Error("Signup response missing access_token — cannot complete workspace creation.");
            }
            localStorage.setItem("vyastra_token", data.access_token);
            toast.success(`Welcome to Vyaahar, ${form.full_name}!`);
            window.location.href = "/";
        } catch (err) {
            // Fail LOUDLY — persistent inline banner + toast + console. Never fail silently.
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            let msg;
            if (detail) {
                msg = detail;
            } else if (err?.message === "Network Error") {
                msg = "Network error — check your connection and try again.";
            } else if (status) {
                msg = `Signup failed (${status}). ${err?.message || ""}`.trim();
            } else {
                msg = err?.message || "Signup failed — please try again or contact support.";
            }
            setSubmitError(msg);
            toast.error(msg);
            console.error("[Signup] create workspace failed:", { status, detail, err });
            if (/verif/i.test(String(msg))) setStep(2);
        } finally { setBusy(false); }
    };

    const stepTitle = step === 1 ? "About you." : step === 2 ? "Verify your email." : "About your business.";
    const stepSub = step === 1
        ? "We'll spin up your workspace with seeded books."
        : step === 2
            ? "We just sent a 6-digit code to your inbox."
            : "We'll preconfigure GST, currency, and your fiscal year.";

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-[#13192e] to-[#1a2240] text-white p-12">
                <img src="/vyaahar-logo-white.png" alt="Vyaahar" className="h-9 object-contain object-left" onError={(e) => { e.target.style.display = "none"; }} />
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a68a56] font-semibold mb-3">START FREE</div>
                    <h2 className="text-[44px] font-semibold leading-[1.05] tracking-tight">Your finance OS, ready in&nbsp;60&nbsp;seconds.</h2>
                    <p className="text-[15px] text-white/70 mt-4 max-w-[460px]">Pre-built Indian Chart of Accounts, GST templates, multi-currency, e-invoicing — out of the box.</p>
                    <ul className="mt-8 space-y-2 text-[13px] text-white/80">
                        <li className="flex gap-2"><Sparkles className="w-4 h-4 text-[#a68a56] mt-0.5" />Double-entry, GST-ready CoA seeded automatically</li>
                        <li className="flex gap-2"><Sparkles className="w-4 h-4 text-[#a68a56] mt-0.5" />AI Copilot answers finance questions on your real data</li>
                        <li className="flex gap-2"><Sparkles className="w-4 h-4 text-[#a68a56] mt-0.5" />FIFO inventory, fixed-asset depreciation, FX revaluation</li>
                    </ul>
                </div>
                <p className="text-[11px] text-white/40">Vyaahar by Vyastics Technologies Private Limited · Encrypted at rest & in transit</p>
            </div>
            <div className="flex flex-col items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-[440px]">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold mb-2.5" data-testid="signup-step-label">CREATE WORKSPACE — STEP {step} OF 3</div>
                    <h1 className="text-[36px] font-semibold tracking-tight leading-tight">{stepTitle}</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-2">{stepSub}</p>

                    <form onSubmit={step === 3 ? submit : step === 2 ? verifyOtp : (e) => { e.preventDefault(); goToVerify(); }} className="mt-8 space-y-4" data-testid="signup-form">
                        {step === 1 && (
                            <>
                                <Field label="Full name" value={form.full_name} onChange={(v) => set("full_name", v)} requiredIndicator testid="signup-name" />
                                <Field label="Work email" type="email" value={form.email} onChange={(v) => { set("email", v); if (emailError) setEmailError(""); }} requiredIndicator testid="signup-email" error={emailError} />
                                <Field
                                    label="Password"
                                    type="password"
                                    value={form.password}
                                    onChange={(v) => {
                                        set("password", v);
                                        // Live validation: show the missing rules the moment the
                                        // user has typed anything but the password is not yet strong.
                                        if (!v) {
                                            setPasswordError("");
                                        } else {
                                            const next = validatePassword(v);
                                            setPasswordError(next.ok ? "" : ("Password must include " + next.missing.join(", ") + "."));
                                        }
                                    }}
                                    requiredIndicator
                                    testid="signup-password"
                                    hint="8+ chars with uppercase, lowercase, number & special character"
                                    error={passwordError}
                                />
                                <Button
                                    type="submit"
                                    className="w-full h-11"
                                    disabled={busy || !form.full_name || !form.email || !validatePassword(form.password).ok}
                                    data-testid="signup-next"
                                >
                                    {busy ? "Sending code…" : (<>Continue <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
                                </Button>
                            </>
                        )}

                        {step === 2 && (
                            <>
                                <div className="rounded-md border border-border bg-card p-3.5 flex items-start gap-3">
                                    <Mail className="w-4 h-4 mt-0.5 text-gold shrink-0" />
                                    <div className="text-[12.5px] leading-relaxed">
                                        <div>Enter the code sent to</div>
                                        <div className="font-mono text-[13px] mt-0.5 break-all" data-testid="verify-email-hint">{form.email}</div>
                                    </div>
                                </div>

                                <div>
                                    <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">6-digit verification code</Label>
                                    <Input
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        pattern="\d{6}"
                                        maxLength={6}
                                        value={otp}
                                        onChange={(e) => {
                                            const cleaned = e.target.value.replace(/\D/g, "").slice(0, 6);
                                            setOtp(cleaned);
                                            if (otpError) setOtpError("");
                                        }}
                                        placeholder="••••••"
                                        data-testid="verify-otp-input"
                                        aria-invalid={!!otpError}
                                        className={`mt-1.5 h-12 text-center text-[22px] tracking-[10px] font-mono ${otpError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                                    />
                                    {otpError && <p className="text-[11px] text-destructive mt-1" data-testid="verify-otp-error">{otpError}</p>}
                                </div>

                                <Button type="submit" className="w-full h-11" disabled={busy || otp.length !== 6} data-testid="verify-otp-submit">
                                    {busy ? "Verifying…" : (<>Verify email <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
                                </Button>

                                <div className="flex items-center justify-between text-[12.5px]">
                                    <button type="button" onClick={() => setStep(1)} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="verify-back">← Change email</button>
                                    <button
                                        type="button"
                                        onClick={resendOtp}
                                        disabled={cooldown > 0 || busy}
                                        className="text-gold hover:underline disabled:no-underline disabled:text-muted-foreground disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                                        data-testid="verify-resend"
                                    >
                                        <RotateCcw className="w-3.5 h-3.5" />
                                        {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                                    </button>
                                </div>
                            </>
                        )}

                        {step === 3 && (
                            <>
                                <Field label="Business name" value={form.org_name} onChange={(v) => set("org_name", v)} requiredIndicator testid="signup-org" />
                                <Field label="Legal name (optional)" value={form.legal_name} onChange={(v) => set("legal_name", v)} testid="signup-legal" />
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Base currency</Label>
                                        <Select value={form.base_currency} onValueChange={(v) => set("base_currency", v)}>
                                            <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                                            <SelectContent>{["INR", "USD", "EUR", "GBP", "AED", "SGD", "AUD"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Industry</Label>
                                        <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                                            <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                                            <SelectContent>{INDUSTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <Field
                                    label="GSTIN"
                                    value={form.gstin}
                                    onChange={(v) => {
                                        const up = v.toUpperCase();
                                        set("gstin", up);
                                        if (gstinError) setGstinError("");
                                    }}
                                    testid="signup-gstin"
                                    requiredIndicator
                                    hint="15-character GSTIN — required for GST-compliant invoicing"
                                    error={gstinError}
                                    maxLength={15}
                                />
                                {submitError && (
                                    <div
                                        role="alert"
                                        data-testid="signup-submit-error"
                                        className="rounded-md border border-destructive/60 bg-destructive/8 text-destructive text-[12.5px] leading-relaxed p-3"
                                    >
                                        {submitError}
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setStep(2)}>Back</Button>
                                    <Button type="submit" disabled={busy} className="flex-1 h-11" data-testid="signup-submit">
                                        {busy ? "Creating workspace…" : (<>Create workspace <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
                                    </Button>
                                </div>
                            </>
                        )}
                    </form>

                    <div className="mt-6 text-center text-[12.5px] text-muted-foreground">
                        Already have an account? <Link to="/login" className="text-gold font-semibold hover:underline">Sign in →</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required, requiredIndicator, testid, hint, error, maxLength }) {
    const showAsterisk = required || requiredIndicator;
    return (
        <div>
            <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                {label}{showAsterisk && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                maxLength={maxLength}
                aria-invalid={!!error}
                data-testid={testid}
                className={`mt-1.5 h-11 ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
            />
            {error ? (
                <p className="text-[11px] text-destructive mt-1" data-testid={`${testid}-error`}>{error}</p>
            ) : hint ? (
                <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>
            ) : null}
        </div>
    );
}
